<template>
  <v-container fluid>
    <TopHeader>
      <template v-slot:title>
        <span class="text-h5">
          Manage / <span class="primary--text">Products</span>
        </span>
      </template>
      <template
        v-slot:right
        v-if="
          $store.getters.isUserAdmin ||
            $store.getters.isBrandUser ||
            $store.getters.isContributor
        "
      >
        <span
          class="subtitle-1 grey--text font-weight-light d-flex"
          style="align-items: flex-end;"
        >
          <v-btn outlined @click="goToCreateProduct" color="primary">
            <v-icon left>mdi-plus</v-icon> New product
          </v-btn>
        </span>
      </template>
    </TopHeader>

    <v-row>
      <v-col class="mt-6 pb-0">
        <v-data-table
          fixed-header
          :height="$vuetify.breakpoint.height - 258"
          :loading="loading"
          class="pos-relative"
          :search="search"
          v-model="selected"
          :headers="headers"
          :items="products"
          :server-items-length="totalProducts"
          :options.sync="paginate"
          item-key="id"
          show-select
          @click:row="openProduct"
          :footer-props="{ itemsPerPageOptions: [100, 150, 200] }"
        >
          <template v-slot:top>
            <v-row>
              <v-col class="d-flex justify-space-between pt-0 pb- px-10">
                <v-text-field
                  :disabled="loading"
                  clearable
                  @click:clear="search = ''"
                  v-model="search"
                  @keyup.enter.native="handleSearchProducts"
                  style="max-width:300px;"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search for a product..."
                />
              </v-col>
              <v-spacer></v-spacer>
              <v-chip outlined>
                <template v-if="loading">
                  <v-progress-circular
                    :size="20"
                    :width="2"
                    :indeterminate="true"
                  ></v-progress-circular>
                </template>
                <template v-else>
                  Validated : {{ validated_kpi }} / {{ total_kpi }}
                </template>
              </v-chip>
            </v-row>
          </template>
          <template v-slot:item.price="{ item }">
            {{ item.price ? item.price + " " + (item.currency || "eur") : "-" }}
          </template>
          <template v-slot:item.families="{ item }">
            <template v-if="item.families">
              <v-chip
                class="ma-1"
                small
                color="default"
                v-for="(n, i) in item.families.split(',')"
                :key="i"
                >{{ n }}</v-chip
              >
            </template>
          </template>
          <template v-slot:item.sub_families="{ item }">
            <template v-if="item.sub_families">
              <v-chip
                class="ma-1"
                small
                color="default"
                v-for="(n, i) in item.sub_families.split(',')"
                :key="i"
                >{{ n }}</v-chip
              >
            </template>
          </template>
          <template v-slot:item.top_notes="{ item }">
            <template v-if="item.top_notes">
              <v-chip
                class="ma-1"
                small
                color="default"
                v-for="(n, i) in item.top_notes.split(',')"
                :key="i"
                >{{ n }}</v-chip
              >
            </template>
          </template>
          <template v-slot:item.middle_notes="{ item }">
            <template v-if="item.middle_notes">
              <v-chip
                class="ma-1"
                small
                color="default"
                v-for="(n, i) in item.middle_notes.split(',')"
                :key="i"
                >{{ n }}</v-chip
              >
            </template>
          </template>
          <template v-slot:item.base_notes="{ item }">
            <template v-if="item.base_notes">
              <v-chip
                class="ma-1"
                small
                color="default"
                v-for="(n, i) in item.base_notes.split(',')"
                :key="i"
                >{{ n }}</v-chip
              >
            </template>
          </template>
          <template v-slot:item.description="{ item }">
            {{ truncate(item.description, { length: 60 }) }}
          </template>

          <template v-slot:item.image_url="{ item }">
            <v-img
              :src="getImgBrand(item)"
              contain
              width="50"
              height="50"
              class="rounded-md ma-2"
            ></v-img>
          </template>

          <!-- TODO -->
          <template v-slot:item.brand_id="{ item }">
            {{ item.brand_name }}
          </template>
          <template v-slot:item.status="{ item }">
            <span v-if="item.request_rejected">
              REJECTED_{{ item.status }}
            </span>
            <span v-else-if="item.status == 'CREATE_REQUEST'">
              Need Perfumist validation for create
            </span>
            <span v-else-if="item.status == 'DELETE_REQUEST'">
              Need Perfumist validation for delete
            </span>
            <span v-else-if="item.status == 'EDIT_REQUEST'">
              Need Perfumist validation for edit
            </span>
            <span v-else-if="item.status == 'DISCONTINUATE_REQUEST'">
              Need Perfumist validation for discontinuate
            </span>
            <span v-else-if="!item.status && !item.validated">
              Need Brand validation
            </span>
            <span v-else-if="!item.status && item.validated">
              Validated
            </span>
          </template>
          <template v-slot:item.validated="{ item }">
            <v-chip :color="item.validated ? 'success' : 'warning'">{{
              item.validated ? "Yes" : "No"
            }}</v-chip>
          </template>
          <template v-slot:item.discontinued="{ item }">
            <v-chip outlined>{{ item.discontinued ? "Yes" : "No" }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small text color="accent" @click="openProduct(item)">
              <v-icon left>mdi-eye</v-icon> view
            </v-btn>
          </template>
          <template v-slot:footer>
            <div class="custom-footer-corner">
              <v-menu top>
                <template v-slot:activator="{ on, attrs }">
                  <span class="grey--text">Actions </span>
                  <v-btn
                    class="ml-4"
                    text
                    color="accent"
                    dark
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ selected.length }} products selected
                    <v-icon right> mdi-menu-up</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <!-- Only brand users can validate -->
                  <v-list-item
                    :disabled="!hasSelectedProducts"
                    v-if="$store.getters.isBrandUser"
                    @click="validate"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-check-all</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Validate</v-list-item-title>
                  </v-list-item>
                  <!-- Only admins & brand users can ask for deletion -->
                  <v-list-item
                    :disabled="!hasSelectedProducts"
                    v-if="$store.getters.isUserAdmin"
                    @click="askForBulkApprove"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-check-decagram-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Approve</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    :disabled="
                      !hasSelectedProducts ||
                        !(
                          $store.getters.isUserAdmin ||
                          $store.getters.isBrandUser
                        )
                    "
                    @click="askForDeletion"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-delete-alert</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Ask for deletion</v-list-item-title>
                  </v-list-item>
                  <!-- Only admins can ask for discontinuation in bulk -->
                  <v-list-item
                    v-if="$store.getters.isUserAdmin"
                    :disabled="!hasSelectedProducts"
                    @click="askForDiscontinuation"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-close-octagon-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>
                      Discontinuate
                    </v-list-item-title
                    >
                  </v-list-item>
                  <!-- Anyone can export -->
                  <v-list-item
                    :disabled="!hasSelectedProducts"
                    @click="exportProducts"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-table-arrow-down</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Export</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { PROPOSAL_STATUS } from "@/data"
import xlsx from "json-as-xlsx"
import { CatalogService, ProductService } from "@/services"
import { debounce, truncate } from "lodash"
import TopHeader from "@/components/base/TopHeader.vue"
export default {
  components: {
    TopHeader
  },
  data() {
    return {
      validated_kpi: null,
      not_validated_kpi: null,
      total_kpi: null,
      loading: true,
      search: "",
      paginate: {},
      totalProducts: 0,
      products: [],
      selected: [],
      headers: [
        {
          value: "validated",
          sortable: true,
          align: "center",
          text: "Validated"
        },
        {
          value: "status",
          sortable: true,
          align: "center",
          text: "Status"
        },
        {
          value: "image_url",
          sortable: false,
          align: "start",
          text: "Image"
        },
        {
          value: "name",
          sortable: true,
          align: "start",
          text: "Product"
        },
        {
          value: "brand_name",
          sortable: true,
          align: "start",
          text: "Brand"
        },
        {
          value: "category",
          sortable: true,
          align: "start",
          text: "Category"
        },
        {
          value: "gender",
          sortable: true,
          align: "start",
          text: "Gender"
        },
        {
          value: "product_type",
          sortable: true,
          align: "start",
          text: "Concentration"
        },
        {
          value: "year",
          sortable: true,
          align: "center",
          text: "Year of launch"
        },
        {
          value: "month",
          sortable: true,
          align: "center",
          text: "Month of launch"
        },
        {
          value: "description",
          sortable: true,
          align: "start",
          text: "Description",
          width: 180
        },
        {
          value: "families",
          sortable: false,
          align: "center",
          text: "Families"
        },
        {
          value: "sub_families",
          sortable: false,
          align: "center",
          text: "Subfamilies"
        },
        {
          value: "top_notes",
          sortable: false,
          align: "center",
          text: "Top notes",
          width: 350
        },
        {
          value: "middle_notes",
          sortable: false,
          align: "center",
          text: "Middle Notes",
          width: 350
        },
        {
          value: "base_notes",
          sortable: false,
          align: "center",
          text: "Base notes",
          width: 350
        },
        {
          value: "discontinued",
          sortable: true,
          align: "center",
          text: "Discontinued"
        },
        {
          value: "",
          sortable: false,
          align: "center",
          text: "Bottle image"
        },
        {
          value: "price",
          sortable: true,
          align: "center",
          text: "Price"
        },
        {
          value: "",
          sortable: false,
          align: "center",
          text: "Packaging image"
        },

        {
          value: "created_at",
          sortable: true,
          align: "start",
          text: "Created",
          width: 150
        },
        {
          value: "updated_at",
          sortable: true,
          align: "start",
          text: "Updated",
          width: 150
        },
        {
          value: "current_requester",
          sortable: false,
          align: "start",
          text: "Current requester",
          width: 150
        },
        {
          value: "last_editor",
          sortable: false,
          align: "start",
          text: "Last editor",
          width: 150
        },
        // { value: "actions", sortable: false, align: "start", text: "Actions" }
      ]
    }
  },
  mounted() {
    this.$watch(
      "$store.state.catalogFilters",
      (vn, vo) => {
        this.paginate.page = 1
        this.handleSearchProducts()
      },
      { deep: true }
    )
    this.$nextTick(() => {
      this.handleSearchProducts()
    })
  },
  computed: {
    hasSelectedProducts() {
      return this.selected.length > 0
    }
  },
  watch: {
    search(vn, vo) {
      if (this.paginate.page > 1) this.paginate.page = 1
      if (this.search == "") {
        this.debounceSearch()
      }
    },
    paginate: {
      deep: true,
      handler(newValue, oldValue) {
        this.$nextTick(() => {
          if (oldValue.page) {
            this.handleSearchProducts()
          }
        })
      }
    }
  },
  methods: {
    truncate,
    goToCreateProduct() {
      this.$router.push({ name: "ProductCreate" })
    },
    handleSearchProducts() {
      let additionalFilters = {}
      const filters = this.$store.state.catalogFilters

      Object.keys(filters).map(k => {
        let v =
          k === "brand_id" ? (filters[k] || []).map(o => o?.id) : filters[k]

        if (v !== null && v !== "") additionalFilters[k] = v
      })

      this.getProducts(additionalFilters)
    },
    debounceSearch: debounce(function() {
      this.handleSearchProducts()
    }, 1000),
    downloadFile(json) {
      var columns = this.headers.map(h => ({ label: h.text, value: h.value }))

      var settings = {
        sheetName: "Sheet1",
        fileName: "perfumist-products"
      }
      xlsx(columns, json, settings)
    },
    getProducts(additionalFilters = {}) {
      const $ = this
      $.loading = true

      // Not need user car searh with brand (if access)
      // const specificFilters = this.$store.getters.isUserAdmin
      //   ? {}
      //   : {
      //       brand__brand_id: this.$store.state.globalBrand.brand_id
      //     }

      // Patch for Brand : search with brand__brand_id
      if (additionalFilters["brand_id"] !== undefined) {
        additionalFilters["brand__id"] = additionalFilters["brand_id"]
        delete additionalFilters["brand_id"]
      }

      let filters = Object.entries(additionalFilters)
        .map(([k, v]) => {
          return `${k}=${v}`
        })
        .join("&")

      let orders = null
      if ($.paginate.sortDesc.length >= 1) {
        let sortParamName =
          $.paginate.sortBy[0] == "brand_name"
            ? "brand__name"
            : $.paginate.sortBy[0]
        orders = $.paginate.sortDesc[0] ? "-" + sortParamName : sortParamName
      }

      const paginate = {
        page: this.paginate.page,
        page_size: this.paginate.itemsPerPage
      }

      CatalogService.list($, this.search, filters, orders, paginate)
        .then(r => {
          $.products = r.results
          $.totalProducts = r.count
        })
        .finally(() => {
          CatalogService.getProductsKPI($, this.search, filters)
            .then(r => {
              $.validated_kpi = r.validated
              $.not_validated_kpi = r.not_validated
              $.total_kpi = r.total
            })
            .catch(() => {
              $.validated_kpi = null
              $.not_validated_kpi = null
              $.total_kpi = null
            })
            .finally(() => {
              $.loading = false
            })
        })
    },
    getImgBrand(item) {
      return item["image_url"]
        ? item["image_url"]
        : "@/assets/logo_black_no_text.png"
    },
    openProduct(curr) {
      // ToDo: Pass history UUID or product ID here
      this.$router.push({
        name: "Product",
        params: { product: curr.product_id || curr.id }
      })
    },
    askForDiscontinuation() {
      if (
        confirm(
          `Are you sure you want to discontinuate these products? ${
            this.$store.getters.isUserAdmin
              ? "(As an admin, this request is approved automatically)"
              : ""
          }`
        )
      ) {
        let selectedProducts = {}
        this.selected.map(p => {
          selectedProducts[p['id']] = p['product_id'] 
        })
        ProductService.bulkDiscontinuate(
          this,
          selectedProducts
        ).then(r => {
          this.handleSearchProducts()
          this.selected = []
        })
      }
    },
    exportProducts() {
      this.downloadFile(this.selected)
      this.$store.commit("success", "Selected items exported !")
    },
    askForDeletion() {
      if (
        confirm(
          `Are you sure you want to submit this request? ${
            this.$store.getters.isUserAdmin
              ? "(As an admin, this request is approved automatically)"
              : ""
          }`
        )
      ) {
        let promises = []
        this.selected.map(p => {
          promises.push(
            ProductService.create(
              this,
              {
                status: PROPOSAL_STATUS.DELETE_REQUEST
              },
              p.id
            )
          )
        })
        Promise.all(promises).then(r => {
          this.handleSearchProducts()

          this.$store.commit(
            "success",
            "Your request has been sent " +
              (promises.length !== this.selected.length
                ? "(pending requests not impacted)"
                : "")
          )
          this.selected = []
        })
      }
    },
    askForBulkApprove() {
      if (confirm("Are you sure you want to approve these requests ?")) {
        const productIds = this.selected.map(p => p.id)

        ProductService.bulkApprove(this, productIds).then(() => {
          this.handleSearchProducts()
          this.$store.commit("success", "Products approved !")
          this.selected = []
        })
      }
    },
    validate() {
      if (confirm("Are you sure you want to submit this request ?")) {
        const productIds = this.selected.map(p => p.id)

        ProductService.bulkValidate(this, productIds).then(() => {
          this.handleSearchProducts()
          this.$store.commit("success", "Products validated !")
          this.selected = []
        })
      }
    }
  }
}
</script>

<style lang="scss">
.v-data-table__wrapper tr {
  cursor: pointer;
}
</style>
<style lang="scss" scoped>
.custom-footer-corner {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 10px 12px;
}
.pos-relative {
  position: relative;
}
</style>
